import type { ComponentType } from "react";
import { z } from "zod";
import { KNOWN_THEME_KEYS } from "../styles/themes";
import { CURRENCY_SYMBOLS } from "../utils/currency";

// Single source of truth for the home-cards URL prefix default.
const DEFAULT_HOME_CARDS_PREFIX = "home_";

// Partner config schema. Validated at module load in src/config/index.ts.
// Adding a new partner: see the comment near RAW_PARTNERS in src/config/index.ts.
//
// `theme.light` / `theme.dark` are PARTIAL overrides merged on top of the base
// palette in src/styles/themes.ts. Partners typically override a small set of
// brand-defining tokens (e.g. primary, secondary, buttonPrimary). Keys must
// match one of the tokens exported from src/styles/themes.ts — typos like
// `primery` are rejected at boot. Overriding non-brand tokens (text, surface,
// semantic colors) is allowed but risks breaking the design system.

const knownThemeKeySet = new Set<string>(KNOWN_THEME_KEYS);
const themeOverrides = z
  .record(
    z.string().refine((k) => knownThemeKeySet.has(k), {
      message: "Unknown theme key (must match a token in styles/themes.ts)",
    }),
    z.string(),
  )
  .optional();

const chatConfigSchema = z.discriminatedUnion("provider", [
  z.object({ provider: z.literal("intercom"), appId: z.string().min(1) }),
  z.object({ provider: z.literal("carrot_quest"), token: z.string().min(1) }),
  z.object({ provider: z.literal("zendesk"), key: z.string().min(1) }),
  z.object({ provider: z.literal("crisp"), websiteId: z.string().min(1) }),
  z.object({ provider: z.literal("none") }),
]);

const signupConfigSchema = z.discriminatedUnion("mode", [
  // Open signup: token field hidden from user; partnerToken is sent on every
  // signup. Requires a MULTI-USE token issued by BE — single-use would only
  // work for the first signup.
  z.object({ mode: z.literal("open"), partnerToken: z.string().min(1) }),
  // Token-required: user pastes the token they were given (current SABO flow).
  // Token can be single-use (per-user invite) or multi-use (shared partner code).
  z.object({ mode: z.literal("token-required") }),
]);

const featuresSchema = z.object({
  shopify: z.boolean(),
  tools: z.boolean(),
  support: z.boolean(),
  delivery: z.boolean(),
  mailbox: z.boolean(),
  // Free-form address entry via /client_api/address_parse_jobs (defaults off).
  addressParsing: z.boolean().default(false),
  // Paste free-form package text to create an inbound shipment.
  inboundParsing: z.boolean().default(false),
  // Receipt/invoice photo → customs declaration rows.
  invoiceParsing: z.boolean().default(false),
  // USDT (TRC-20) balance deposits in Books and as a way to pay consolidations
  // (defaults off). The backend has no gate of its own and credits USDT 1:1
  // into the account currency, so enable it for USD partners only.
  usdtDeposits: z.boolean().default(false),
  // Telegram bot link management in Account settings (defaults off). The deep
  // link targets the one bot the backend is configured with, so enable it only
  // for the partner that bot is branded for.
  telegramLink: z.boolean().default(false),
  // Receive-only US SMS numbers under /utilities/sms (defaults off). The
  // backend has no partner gate and debits the USD monthly price 1:1 from the
  // account balance, so useSmsNumbersEnabled also requires a USD account.
  smsNumbers: z.boolean().default(false),
});

// Per-partner payment-method availability. Refine guards against the
// "both off" configuration — would leave the user with no way to pay.
// Voucher is intentionally unconditional and not modelled here.
const paymentsConfigSchema = z
  .object({
    creditCard: z.boolean(),
    paypal: z.boolean(),
    overpay: z.boolean().default(true),
    stripe: z.boolean().default(false),
  })
  .refine((p) => p.creditCard || p.paypal || p.overpay || p.stripe, {
    message: "At least one payment method must be true",
  });

export const partnerConfigSchema = z.object({
  partnerId: z.string().regex(/^[a-z][a-z0-9_-]*$/),
  // BE partners-table integer FK. Sent as `partner_id` on POST /client_api/auth
  // when set. Omit for the base brand (sabo) — absence == unattributed signup.
  apiPartnerId: z.number().int().positive().optional(),
  // Prefix used to render the customer identifier ({prefix}{ffid}, e.g.
  // "SB6866"). Shown in the mailbox card, chat handle, header, shipping
  // forms, etc. — consumed by authSelectFfid in globalSelectors.js.
  ffidPrefix: z
    .string()
    .regex(/^[A-Z]{1,4}$/, "ffidPrefix must be 1–4 uppercase letters")
    .default("SB"),
  appName: z.string().min(1),

  meta: z.object({
    title: z.string().min(1),
    description: z.string(),
    canonicalUrl: z.string().url(),
    themeColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    ogImagePath: z.string(),
  }),

  // Logo metadata + React component. Component is validated as "is a function"
  // (covers both function and class components) — Zod can't structurally
  // validate JSX rendering, but the function check catches the common
  // misconfiguration of forgetting to import the logo.
  logo: z.object({
    alt: z.string(),
    Component: z.custom<ComponentType>((val) => typeof val === "function", {
      message: "logo.Component must be a React component (function)",
    }),
  }),

  theme: z.object({
    light: themeOverrides,
    dark: themeOverrides,
  }),

  chat: chatConfigSchema,
  features: featuresSchema,
  payments: paymentsConfigSchema,
  currency: z
    .object({
      code: z.enum(["USD", "EUR"]),
      symbol: z.enum(["$", "€"]),
    })
    // Single source of truth for the pairing lives in utils/currency.ts —
    // a config declaring {code: "USD", symbol: "€"} must fail at parse time.
    .refine((currency) => CURRENCY_SYMBOLS[currency.code] === currency.symbol, {
      message: "currency.symbol must match currency.code",
    }),
  signup: signupConfigSchema,

  // What an anonymous visitor gets at "/". "signin" = redirect to /signin
  // (today's behaviour, the default). "landing" = the marketing landing in
  // src/containers/Landing plus /privacy, /refund-policy and /prohibited-items.
  // The landing content is SABO-specific — a partner opting in must ship its
  // own copy there first.
  publicRoot: z.enum(["signin", "landing"]).default("signin"),

  // Strict allowlist of branch sb_name values to display in the top nav.
  // GET /branches may return more branches than this partner should see; the
  // selector in src/reduxFolder/selectors/warehouseSelectors.js filters to
  // only the names listed here. Adding a new branch on the BE requires a
  // matching config update — that is by design (deliberate gating).
  branches: z.object({
    sbNames: z.array(z.string().min(1)).min(1),
  }),

  // Strict allowlist of mailbox country values. GET /mailboxes may return
  // mailboxes for countries this partner shouldn't see; the selector in
  // src/reduxFolder/selectors/mailboxSelectors.js filters to only the
  // countries listed here. Multiple mailboxes per country are all displayed
  // (the filter is `country in allowlist`, not "one per country").
  mailboxes: z.object({
    countries: z.array(z.string().min(1)).min(1),
  }),

  // API-surface overrides. Required so partners declare their KB namespace
  // explicitly rather than silently inheriting SABO's 'home_' prefix. The
  // inner default on homeCardsPrefix still applies if a partner writes
  // `api: {}` — sensible when sharing SABO's KB.
  api: z.object({
    // Override the auto-detected backend base URL. Leave unset to use the
    // hostname-based routing in src/api/transport.ts.
    baseUrl: z.string().url().optional(),
    // Path segment that prefixes the locale in home-card endpoints:
    //   GET /kb/{homeCardsPrefix}{locale}/...
    // SABO uses 'home_' so URLs resolve as /kb/home_en/, /kb/home_ru/.
    // Partners with their own KB namespace can override (e.g. 'partner_home_').
    homeCardsPrefix: z
      .string()
      .regex(/^[a-z0-9_-]+$/)
      .default(DEFAULT_HOME_CARDS_PREFIX),
  }),

  sentry: z
    .object({
      tags: z.record(z.string(), z.string()).default({}),
    })
    .default({ tags: {} }),
});

// Output (post-validation) — what consumers see via useBranding(). Defaults applied.
export type PartnerConfig = z.infer<typeof partnerConfigSchema>;
// Input (pre-validation) — shape partner files declare. Optional fields with
// defaults can be omitted.
export type PartnerConfigInput = z.input<typeof partnerConfigSchema>;
export type ChatConfig = z.infer<typeof chatConfigSchema>;
export type SignupConfig = z.infer<typeof signupConfigSchema>;
export type PartnerFeatures = z.infer<typeof featuresSchema>;
export type PaymentsConfig = z.infer<typeof paymentsConfigSchema>;
